import type { SafetyPortfolioResponse } from '../../services/reportsApi';
import type { WorkspaceAttentionItem } from './workspaceAttention';
import {
  buildSiteComparisonRows,
  formatPortfolioStateDetail,
} from './workspacePortfolioSiteComparison';
import { formatOwnershipLabel } from './workspacePortfolioOwnership';
import { formatSiteCoverageSummary } from './workspacePortfolioAttention';

export interface PortfolioExportContext {
  exportedAt: string;
  timeWindowLabel?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  attentionFilter?: string | null;
  savedView?: string | null;
}

export interface PortfolioExportInput {
  portfolio: SafetyPortfolioResponse;
  attentionItems: WorkspaceAttentionItem[];
  context: PortfolioExportContext;
}

const FORBIDDEN_EXPORT_COLUMNS = [
  'risk_score',
  'danger_score',
  'safety_score',
  'aggregate_score',
  'ranking',
  'rank',
];

function normalize(value?: string | null): string {
  return String(value || '').trim();
}

export function escapeCsvField(value: string | number | null | undefined): string {
  const text = value === null || value === undefined ? '' : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function csvRow(values: Array<string | number | null | undefined>): string {
  return values.map((value) => escapeCsvField(value)).join(',');
}

function buildDisclaimerLines(input: PortfolioExportInput): string[] {
  const { portfolio, attentionItems, context } = input;
  const partial = Boolean(portfolio.permissions?.partial || portfolio.scope?.partial);
  const limit = Number(portfolio.limit ?? 0);
  const attentionTotal = (portfolio.attention || []).length;
  const lines = [
    '# Safety Control Workspace — organization portfolio export',
    '# This export reflects the loaded portfolio view in the browser, not a recomputed org-wide aggregate.',
    '# Counts are operational workload indicators only — not a risk score, danger score, safety score, ranking, or safety-performance conclusion.',
    '# Do not treat denied, unavailable, or unloaded sites as zero-risk or as complete org totals.',
    `# Portfolio coverage summary: ${formatSiteCoverageSummary(portfolio.site_coverage)}`,
    `# Partial portfolio: ${partial ? 'yes' : 'no'}`,
  ];
  if (partial) {
    lines.push('# Permission-denied or unavailable sites are listed in site_coverage but are not mixed into Attention counts.');
    const reasons = [
      ...(portfolio.scope?.partial_reasons || []),
      ...(portfolio.permissions?.denied_sites || []).map((site) => {
        const siteId = normalize(site.site_id) || 'site';
        const reason = normalize(site.reason) || 'permission_denied';
        return `${siteId}: ${reason}`;
      }),
    ].filter(Boolean);
    if (reasons.length > 0) {
      lines.push(`# Partial reasons: ${reasons.join('; ')}`);
    }
  }
  const interpretation = portfolio.interpretation?.counts_require_context || [];
  if (interpretation.length > 0) {
    lines.push(`# Counts require context: ${interpretation.join('; ')}`);
  }
  if (limit > 0 && attentionTotal >= limit) {
    lines.push(`# Attention list may be truncated to portfolio limit (${limit}). Exported attention rows: ${attentionItems.length} of up to ${limit} loaded.`);
  } else {
    lines.push(`# Exported attention rows: ${attentionItems.length}.`);
  }
  if (context.timeWindowLabel) {
    lines.push(`# Time window: ${context.timeWindowLabel}`);
  }
  if (context.reportFrom || context.reportTo) {
    lines.push(`# Report window: ${context.reportFrom || '—'} to ${context.reportTo || '—'}`);
  }
  if (context.attentionFilter) {
    lines.push(`# Attention filter: ${context.attentionFilter}`);
  }
  if (context.savedView) {
    lines.push(`# Saved view: ${context.savedView}`);
  }
  lines.push(`# Exported at: ${context.exportedAt}`);
  return lines;
}

function buildMetadataSection(input: PortfolioExportInput): string[] {
  const { portfolio, context } = input;
  const rows = [
    '',
    '# METADATA',
    csvRow(['field', 'value']),
    csvRow(['org_id', portfolio.org_id || '']),
    csvRow(['scope_type', portfolio.scope?.scope_type || 'organization']),
    csvRow(['sites_requested', portfolio.summary?.sites_requested ?? '']),
    csvRow(['sites_included', portfolio.summary?.sites_included ?? '']),
    csvRow(['sites_denied', portfolio.summary?.sites_denied ?? '']),
    csvRow(['sites_unavailable', portfolio.summary?.sites_unavailable ?? '']),
    csvRow(['portfolio_partial', portfolio.permissions?.partial || portfolio.scope?.partial ? 'yes' : 'no']),
    csvRow(['portfolio_limit', portfolio.limit ?? '']),
    csvRow(['attention_loaded_count', (portfolio.attention || []).length]),
    csvRow(['attention_exported_count', input.attentionItems.length]),
    csvRow(['time_window', context.timeWindowLabel || '']),
    csvRow(['report_from', context.reportFrom || '']),
    csvRow(['report_to', context.reportTo || '']),
    csvRow(['attention_filter', context.attentionFilter || '']),
    csvRow(['saved_view', context.savedView || '']),
    csvRow(['exported_at', context.exportedAt]),
    csvRow(['opaque_aggregate_fields', portfolio.interpretation?.opaque_aggregate_fields || 'not_exported']),
  ];
  return rows;
}

function buildSiteCoverageSection(portfolio: SafetyPortfolioResponse): string[] {
  const header = [
    'site_id',
    'display_name',
    'region',
    'site_owner',
    'data_state',
    'state_detail',
    'included',
    'workflow_count',
    'attention_count',
    'data_freshness_at',
  ];
  FORBIDDEN_EXPORT_COLUMNS.forEach((column) => {
    if (header.includes(column)) {
      throw new Error(`Forbidden export column: ${column}`);
    }
  });
  const rows = [
    '',
    '# SITE_COVERAGE',
    csvRow(header),
    ...buildSiteComparisonRows(portfolio.site_coverage).map((row) => csvRow([
      row.siteId,
      row.displayName,
      row.regionLabel,
      row.siteOwner,
      row.dataStateLabel,
      row.stateDetail || formatPortfolioStateDetail(
        (portfolio.site_coverage || []).find((site) => normalize(site.site_id) === row.siteId) || {},
      ) || '',
      row.included ? 'yes' : 'no',
      row.workflowCount ?? '',
      row.attentionCount ?? '',
      row.freshnessAt || '',
    ])),
  ];
  return rows;
}

function buildAttentionSection(attentionItems: WorkspaceAttentionItem[]): string[] {
  const header = [
    'site_id',
    'site_display_name',
    'region',
    'site_owner',
    'item_label',
    'item_type',
    'work_state',
    'work_owner',
    'owner_identity',
    'location',
    'evidence',
    'updated_at',
    'metric_key',
  ];
  const rows = [
    '',
    '# ATTENTION_ITEMS',
    csvRow(header),
    ...attentionItems.map((item) => csvRow([
      item.siteId || '',
      item.location.split(' · ')[0] || '',
      item.regionLabel ? formatOwnershipLabel(item.regionLabel) : '',
      item.siteOwner ? formatOwnershipLabel(item.siteOwner) : '',
      item.itemLabel,
      item.itemType,
      item.workState,
      item.owner,
      item.ownerIdentity || '',
      item.location,
      item.evidence,
      item.updatedAt || '',
      item.metricKey,
    ])),
  ];
  return rows;
}

export function buildPortfolioExportCsv(input: PortfolioExportInput): string {
  const lines = [
    ...buildDisclaimerLines(input),
    ...buildMetadataSection(input),
    ...buildSiteCoverageSection(input.portfolio),
    ...buildAttentionSection(input.attentionItems),
    '',
  ];
  for (const line of lines) {
    if (!line.startsWith('site_id,') && !line.includes(',site_id,')) continue;
    const header = line.toLowerCase();
    for (const column of FORBIDDEN_EXPORT_COLUMNS) {
      if (header.includes(column)) {
        throw new Error(`Portfolio export must not include ${column}`);
      }
    }
  }
  return lines.join('\n');
}

export function buildPortfolioExportFilename(orgId: string | null | undefined, exportedAt: string): string {
  const org = normalize(orgId) || 'org';
  const stamp = normalize(exportedAt).replace(/[:.]/g, '-');
  return `safety_portfolio_${org}_${stamp || 'export'}.csv`;
}

export function createPortfolioExportBlob(csv: string): Blob {
  return new Blob([csv], { type: 'text/csv;charset=utf-8' });
}
